import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CacheMechanism, LocalizeParser, LocalizeRouterModule, LocalizeRouterSettings } from '@gilsdav/ngx-translate-router';
import { LocalizeRouterHttpLoader } from '@gilsdav/ngx-translate-router-http-loader';
import { TranslateService } from '@ngx-translate/core';

import { featuresWithPermissions } from '../shared/data/permissions';
import { AuthGuard } from '../shared/guards/auth.guard';
import { PermissionsGuard } from '../shared/guards/permissions.guard';

export const createLocalizeRouterHttpLoader = (
    translate: TranslateService,
    location: Location,
    settings: LocalizeRouterSettings,
    http: HttpClient
): LocalizeRouterHttpLoader => new LocalizeRouterHttpLoader(translate, location, settings, http);

export const defaultLanguage = (languages: string[], cachedLang?: string, browserLang?: string): string => {
    let result: string = 'fr';
    if (cachedLang && languages.indexOf(cachedLang) > -1) {
        result = cachedLang;
    } else if (browserLang && languages.indexOf(browserLang) > -1) {
        result = browserLang;
    }
    return result;
};

const routes: Routes = [
    { path: '', redirectTo: '/messaging/received-messages', pathMatch: 'full' },
    {
        path: 'auth',
        loadChildren: () => import('../views/auth/auth.module').then((m: any) => m.AuthModule),
    },
    {
        path: 'messaging',
        loadChildren: () => import('../views/messaging/messaging.module').then((m: any) => m.MessagingModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'repertoire',
        loadChildren: () => import('../views/repertoire/repertoire.module').then((m: any) => m.RepertoireModule),
        canActivate: [AuthGuard],
    },{
        path: 'directory',
        loadChildren: () => import('../views/repertoire/repertoire.module').then((m: any) => m.RepertoireModule),
        canActivate: [AuthGuard],
    },
    /* for mobile nav - redirect to dashboard */
    { path: 'shopping', redirectTo: '/shopping/dashboard', pathMatch: 'full' },
    {
        path: 'shopping',
        children: [
            { path: 'dashboard', loadChildren: () => import('../views/wallet/wallet.module').then((m: any) => m.WalletModule) },
            { path: 'meals', loadChildren: () => import('../views/meals/meals.module').then((m: any) => m.MealsModule) },
            { path: 'nursery', loadChildren: () => import('../views/nursery/nursery.module').then((m: any) => m.NurseryModule) },
            {
                path: 'activities',
                loadChildren: () => import('../views/activities/activities.module').then((m: any) => m.ActivitiesModule),
            },
            {
                path: 'homework-school',
                loadChildren: () => import('../views/homework-school/homework-school.module').then((m: any) => m.HomeworkSchoolModule),
            },
        ],
        data: { permission: featuresWithPermissions.shopping_nav },
        canActivate: [AuthGuard, PermissionsGuard],
    },
    {
        path: 'my-account',
        loadChildren: () => import('../views/my-account/my-account.module').then((m: any) => m.MyAccountModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'cloud',
        loadChildren: () => import('../views/cloud/cloud.module').then((m: any) => m.CloudModule),
        canActivate: [AuthGuard],
    },
    {
        path: 'settings',
        loadChildren: () => import('../views/settings/settings.module').then((m: any) => m.SettingsModule),
        data: { permission: featuresWithPermissions.admin_settings_nav },
        canActivate: [AuthGuard, PermissionsGuard],
    },
    // {
    //     path: 'calendar',
    //     loadChildren: () => import('../views/calendar/calendar.module').then((m) => m.CalendarModule),
    //     canActivate: [AuthGuard],
    // },
    { path: '**', redirectTo: '/messaging/received-messages' },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' }),
        LocalizeRouterModule.forRoot(routes, {
            cacheMechanism: CacheMechanism.Cookie,
            cacheName: 'defaultLangWASO',
            alwaysSetPrefix: true,
            defaultLangFunction: defaultLanguage,
            parser: {
                provide: LocalizeParser,
                useFactory: createLocalizeRouterHttpLoader,
                deps: [TranslateService, Location, LocalizeRouterSettings, HttpClient],
            },
        }),
    ],
    exports: [RouterModule, LocalizeRouterModule],
})
export class AppRoutingModule {}
